import Vue from 'vue'
import Vuex from 'vuex'
import firebase from 'firebase/app'
import 'firebase/auth'
import 'firebase/database'
import 'firebase/storage'
import 'firebase/functions'
import sortBy from 'lodash/sortBy'
import moment from 'moment-timezone'
import router from '@/router/'

Vue.use(Vuex)

export const store = new Vuex.Store({
  state: {
    user: null,
    group: null,
    list1: [],
    list2: [],
    list3: [],
    list4: [],
    list5: [],
    list6: [],
    loading: false,
    error: null,
    site: {
      url: `${location.protocol}//${window.location.hostname}`
    },
    clients: [],
    notifications: [],
    latestJobKey: '',
    notificationText: '',
    notificationQueue: [],
    notification: false,
    showSplitInvoiceModal: false,
    splitInvoiceModalJob: {},
    showMultiMergeModal: false,
    multiMergeModalJob: {},
    showJobDetailModal: false,
    jobDetailModalJob: {},
    bankTransactions: [],
    jobFilter: ''
  },
  mutations: {
    setUser (state, payload) {
      state.user = payload
    },
    setGroup (state, payload) {
      state.group = { ...state.group, ...payload }
    },
    clearGroup (state) {
      state.group = null
    },
    setLoadedJobs (state, { payload, listId }) {
      state['list' + listId] = payload
    },
    setClients (state, payload) {
      state.clients = payload
    },
    setNotifications (state, payload) {
      state.notifications = payload
    },
    setLoading (state, payload) {
      state.loading = payload
    },
    setError (state, payload) {
      state.error = payload
    },
    clearError (state) {
      state.error = null
    },
    setLatestJobKey (state, payload) {
      state.latestJobKey = payload
    },
    addNotificationToQueue (state, payload) {
      state.notificationQueue.push(payload)
    },
    setNotificationState (state, payload) {
      state.notification = payload
    },
    setNotificationText (state, payload) {
      state.notificationText = payload
    },
    setNotificationQueue (state, payload) {
      state.notificationQueue = payload
    },
    toggleShowSplitInvoiceModal (state) {
      state.showSplitInvoiceModal = !state.showSplitInvoiceModal
    },
    setSplitInvoiceModalJob (state, payload) {
      state.splitInvoiceModalJob = payload
    },
    toggleShowMultiMergeModal (state) {
      state.showMultiMergeModal = !state.showMultiMergeModal
    },
    setMultiMergeModalJob (state, payload) {
      state.multiMergeModalJob = payload
    },
    toggleShowJobDetailModal (state) {
      state.showJobDetailModal = !state.showJobDetailModal
    },
    setJobDetailModalJob (state, payload) {
      state.jobDetailModalJob = payload
    },
    setJobFilter (state, payload) {
      state.jobFilter = payload
    }
  },
  actions: {
    async signUserUp ({ commit, dispatch }, payload) {
      // start loader
      commit('setLoading', true)
      // clear all previous errors
      commit('clearError')
      try {
        // get user from Auth service
        const user = await firebase.auth().createUserWithEmailAndPassword(payload.email, payload.password)
        // get key for new user node in FB
        var newGroupKey = firebase.database().ref('groups').push().key
        // creater a default user object
        const newUser = {
          id: user.user.uid,
          email: user.user.email,
          group: newGroupKey
        }
        // add the user to the users table
        await firebase.database().ref(`/users/${newUser.id}`).set({ group: newGroupKey, email: newUser.email })

        // set the end of trial date
        let endOfTrial = moment().utc().add(14, 'days').toISOString()

        // create default newGroup object
        const newGroup = {
          archiveJobsAfterDays: '30',
          autoIncrement: true,
          autoIncrementPrefix: 'INV-',
          autoQuoteIncrement: true,
          autoQuoteIncrementPrefix: 'QUO-',
          chargeTax: payload.chargeTax,
          chosenAlertTimeScale: '30',
          created: moment().utc().toISOString(),
          currCode: payload.cur,
          currSymbol: payload.symbol,
          cntc: payload.cntc,
          cntn: payload.cntn,
          companyAddress: '',
          groupAddress1: '',
          groupAddress2: '',
          groupCityRegion: '',
          groupCountyState: '',
          groupPostcode: '',
          groupCountry: '',
          displayWithTax: payload.displayWithTax,
          email: newUser.email,
          isFirstInvoice: true,
          isFirstQuote: true,
          lang: payload.lang,
          lastSignedIn: moment().utc().toISOString(),
          logo: '',
          logoAlign: 'center',
          logoWidth: '160',
          logoName: '',
          logoNameSize: '20',
          nextNumber: '00000001',
          nextQuoteNumber: '00000001',
          noOfSignIns: 1,
          paymentTerms: 'e.g. Payment must be made within x days.\nFor Bank Transfer use the following account: \nSort code: xx-xx-xx \nAccount no: xxxxxxxx',
          rgnc: payload.rgnc,
          rgnn: payload.rgnn,
          subscription: {
            access: {
              level: 'pro'
            },
            cardBrand: '',
            status: 'trial',
            last4: '',
            token: '',
            current_period_end: endOfTrial,
            current_period_start: moment().utc().toISOString(),
            shutdown_date: endOfTrial
          },
          taxCalcInclusive: payload.taxCalcInclusive,
          taxLabel: 'VAT',
          taxRate: '20',
          taxRates: [
            { rate: '20' },
            { rate: '5' },
            { rate: '0' }
          ],
          title: payload.groupTitle,
          tz: payload.tz,
          useLogo: false
        }
        // save the group to FB
        await firebase.database().ref(`/groups/${newGroupKey}`).set(newGroup)

        // commit the group to the store
        commit('setGroup', newGroup)
        // commit the user to the store thus triggering a page reload to the dash
        commit('setUser', newUser)
        // load clients and jobs - will be empty at first but are now listening
        // load clients
        console.log('loadClients')
        dispatch('loadClients')
        // start the job listeners
        console.log('loadJobs')
        dispatch('loadJobs')
        // load notifications
        console.log('loadNotifications')
        dispatch('loadNotifications')
        // enjoy

        firebase.analytics().logEvent('StartTrial')
        window.fbq('track', 'StartTrial', { value: '0.00', currency: 'USD', predicted_ltv: '0.00' })
        commit('setLoading', false)
      } catch (error) {
        commit('setLoading', false)
        commit('setError', error)
      }
    },
    signUserIn ({ commit, dispatch }, payload) {
      commit('setLoading', true)
      commit('clearError')
      // all we do is run the login which sets the users auth state and catch any errors
      // we don't trigger intitialise user here, instead we rely on onAuthStateChanged
      // in the main.js file to detect the user is logged in. THis then dispatches the initialize user
      firebase.auth().signInWithEmailAndPassword(payload.email, payload.password)
        .catch(
          error => {
            commit('setLoading', false)
            commit('setError', error)
          }
        )
    },
    autoSignIn ({ commit, dispatch }, payload) {
      // commit('setUser', {id: payload.uid, email: payload.email})
      dispatch('initialiseUser', payload)
    },
    initialiseUser ({ commit, getters, dispatch }, payload) {
      // get groupid from user's node
      firebase.database().ref('users/' + payload.uid).once('value')
        .then(data => {
          const usersGroupId = data.val().group
          const minimisedView = data.val().minimisedView || false
          firebase.database().ref('groups/' + usersGroupId).once('value', function (snap) {
            // check for end of trial
            // we will handle this in a cron routine but for now we manually check and set
            // boolean to determine whether current trial period end has past
            const trialExpired = moment(snap.val().subscription.current_period_end).isBefore(moment())
            // if it has then lets mark the account as trialExpired and save it
            if ((snap.val().subscription.status === 'trial') && trialExpired) {
              // create new group object
              const newGroup = {
                ...snap.val(),
                subscription: {
                  ...snap.val().subscription,
                  status: 'trialExpired'
                }
              }
              // commit it to store
              commit('setGroup', newGroup)
              // save it to FB
              firebase.database().ref('groups').child(usersGroupId).update(newGroup)
            } else {
              // else all is okay lets just set the group to store and continue...
              commit('setGroup', snap.val())
            }
            // now set the user in store
            commit('setUser', { id: payload.uid, email: payload.email, group: usersGroupId, minimisedView: minimisedView })
            // load clients
            dispatch('loadClients')
            // load jobs
            dispatch('loadJobs')
            // load notifications
            dispatch('loadNotifications')
            // log signIn
            dispatch('logSignIn', { groupId: usersGroupId, noOfSignIns: snap.val().noOfSignIns || 1 })
            // enjoy
          })
        })
        .catch(
          (error) => {
            commit('setError', error)
            commit('setLoading', false)
          }
        )
    },
    async logSignIn ({ commit }, { groupId, noOfSignIns }) {
      let signIns = 1
      signIns = parseFloat(noOfSignIns) + 1
      if (groupId === '' || groupId === undefined) {
        return false
      }
      const payload = {
        lastSignedIn: moment().utc().toISOString(),
        noOfSignIns: signIns
      }

      try {
        return await firebase.database().ref('groups').child(groupId).update(payload)
      } catch (error) {
        commit('setError', 'No group id')
        Promise.reject(error)
      }
    },
    async logout ({ commit, getters }) {
      await firebase.auth().signOut()
      await router.push({ name: 'SignIn' })
      firebase.database().ref(`clients/${getters.user.group}`).off()
      firebase.database().ref(`bank-transactions-matches/${getters.user.group}`).off()
      firebase.database().ref(`notifications/${getters.user.group}`).off()
      commit('setUser', null)
      commit('clearGroup')
      commit('setClients', null)
      commit('setNotifications', null)
      commit('setLoading', false)
      commit('setLoadedJobs', { payload: null, listId: 1 })
      commit('setLoadedJobs', { payload: null, listId: 2 })
      commit('setLoadedJobs', { payload: null, listId: 3 })
      commit('setLoadedJobs', { payload: null, listId: 4 })
      commit('setLoadedJobs', { payload: null, listId: 5 })
      commit('setLoadedJobs', { payload: null, listId: 6 })
      // this refreshes the page to ensure all data is cleared
      // https://github.com/vuejs/vuex/issues/1118#issuecomment-874995593
      window.location.reload()
    },
    async updateGroupSettings ({ commit, getters }, payload) {
      commit('setLoading', true)
      // this runs as a promise because invoice.vue needs to know when the logo is updated
      // as it updates these settings glovally when done in an invoice
      return new Promise((resolve, reject) => {
        const groupId = getters.user.group
        if (payload.newLogo) {
          const filename = payload.newLogo.name
          const ext = filename.slice(filename.lastIndexOf('.'))
          // Create a root reference
          var storageRef = firebase.storage().ref()
          var uploadTask = storageRef.child('groups/' + groupId + '.' + ext).put(payload.newLogo)
          // Register three observers:
          // 1. 'state_changed' observer, called any time the state changes
          // 2. Error observer, called on failure
          // 3. Completion observer, called on successful completion
          uploadTask.on('state_changed', function (snapshot) {
            // Observe state change events such as progress, pause, and resume
            // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
            var progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            console.log('Upload is ' + progress + '% done')
            switch (snapshot.state) {
              case firebase.storage.TaskState.PAUSED: // or 'paused'
                console.log('Upload is paused')
                break
              case firebase.storage.TaskState.RUNNING: // or 'running'
                console.log('Upload is running')
                break
            }
          }, function (error) {
            // Handle unsuccessful uploads
            console.log(error)
          }, function () {
            // Handle successful uploads on complete
            // For instance, get the download URL: https://firebasestorage.googleapis.com/...
            uploadTask.snapshot.ref.getDownloadURL().then(function (downloadURL) {
              console.log('File available at', downloadURL)
              payload.logo = downloadURL
              firebase.database().ref('groups').child(groupId).update(payload)
                .then(() => {
                  commit('setLoading', false)
                  commit('setGroup', payload)
                  resolve('true')
                })
                .catch(error => {
                  commit('setError', error)
                  reject(error)
                  commit('setLoading', false)
                })
            })
          })
        } else {
          firebase.database().ref('groups').child(groupId).update(payload)
            .then(() => {
              commit('setLoading', false)
              commit('setGroup', payload)
              resolve('true')
            })
            .catch(error => {
              commit('setError', error)
              reject(error)
              commit('setLoading', false)
            })
        }
      })
    },
    reloadGroup ({ commit, getters }) {
      commit('setLoading', true)
      const groupId = getters.user.group
      firebase.database().ref('groups/' + groupId).on('value', function (snap) {
        commit('setGroup', snap.val())
        commit('setLoading', false)
      })
    },
    updateEmail ({ commit, getters }, payload) {
      commit('setLoading', true)
      // get usr's id
      const UserId = getters.user.id
      // update the FB Auth
      firebase.auth().currentUser.updateEmail(payload)
        .then(() => {
          // then make an object to update the FB user table
          const emailUpdate = {
            email: payload
          }
          firebase.database().ref('users').child(UserId).update(emailUpdate)
            .then(() => {
              // if good update the local state by using the spread to maintain the group and id
              commit('setUser', {
                ...getters.user,
                email: payload
              })
              // all done stop loading
              commit('setLoading', false)
              this.dispatch('addNotification', { text: 'Email updated.', type: 'primary' })
            })
            .catch(error => {
              commit('setError', error)
              commit('setLoading', false)
            })
        })
        .catch(error => {
          commit('setError', error)
          commit('setLoading', false)
        })
    },
    updateUser ({ commit, getters }, payload) {
      console.log('doing it')
      commit('setLoading', true)
      // get usr's id
      const UserId = getters.user.id
      firebase.database().ref('users').child(UserId).update(payload)
        .then(() => {
          // if good update the local state by using the spread to maintain the group and id
          commit('setUser', payload)
          // all done stop loading
          commit('setLoading', false)
        })
        .catch(error => {
          commit('setError', error)
          commit('setLoading', false)
        })
    },
    updatePassword ({ commit }, payload) {
      commit('setLoading', true)
      firebase.auth().currentUser.updatePassword(payload)
        .then(() => {
          commit('setLoading', false)
          this.dispatch('addNotification', { text: 'Password updated.', type: 'primary' })
        })
        .catch(error => {
          commit('setError', error)
          commit('setLoading', false)
        })
    },
    loadJobs ({ commit, getters }) {
      commit('setLoading', true)
      firebase.database().ref('lists/' + getters.user.group + '/list1').on('value', function (data) {
        const jobs = []
        const obj = data.val()
        for (let key in obj) {
          jobs.push(obj[key])
        }
        commit('setLoadedJobs', { payload: jobs, listId: '1' })
        commit('setLoading', false)
      })

      firebase.database().ref('lists/' + getters.user.group + '/list2').on('value', function (data) {
        const jobs = []
        const obj = data.val()
        for (let key in obj) {
          jobs.push(obj[key])
        }
        commit('setLoadedJobs', { payload: jobs, listId: '2' })
        commit('setLoading', false)
      })

      firebase.database().ref('lists/' + getters.user.group + '/list3').on('value', function (data) {
        const jobs = []
        const obj = data.val()
        for (let key in obj) {
          jobs.push(obj[key])
        }
        commit('setLoadedJobs', { payload: jobs, listId: '3' })
        commit('setLoading', false)
      })

      firebase.database().ref('lists/' + getters.user.group + '/list4').on('value', function (data) {
        const jobs = []
        const obj = data.val()
        for (let key in obj) {
          jobs.push(obj[key])
        }
        commit('setLoadedJobs', { payload: jobs, listId: '4' })
        commit('setLoading', false)
      })

      firebase.database().ref('lists/' + getters.user.group + '/list5').on('value', function (data) {
        const jobs = []
        const obj = data.val()
        for (let key in obj) {
          jobs.push(obj[key])
        }
        commit('setLoadedJobs', { payload: jobs, listId: '5' })
        commit('setLoading', false)
      })
    },
    addJob ({ commit, getters }, { list, order }) {
      commit('setLoading', true)
      const listId = list
      const lowestOrder = order
      // get key
      const ref = firebase.database().ref(`lists/${getters.user.group}/list${listId}`).push().key
      // get key for default line Item
      const lineItemRef = firebase.database().ref(`lists/${getters.user.group}/list${listId}/${ref}/lineItems/`).push().key
      // not ethe latest job jey for immediately opening the job
      commit('setLatestJobKey', ref)

      // build object with a default line item
      const job = {
        amount: '0.00',
        chase_date: '',
        clientName: '',
        contact: '',
        creatorId: getters.user.id,
        deadline_date: '',
        id: ref,
        invoiceId: '',
        invoice_ref: '',
        invoiced_date: '',
        lineItems: {
          [lineItemRef]: {
            amount: '0.00',
            creatorId: getters.user.id,
            description: 'Untitled Job',
            id: lineItemRef,
            lineTotal: '0.00',
            taxPortion: '0.00',
            taxRate: getters.group.taxRate
          }
        },
        listId: listId,
        marked: '',
        note: '',
        notes: {},
        order: lowestOrder,
        paid_date: '',
        start_date: '',
        taxIncluded: getters.group.taxCalcInclusive,
        times: {},
        title: 'Untitled Job',
        totalNetAmount: 0,
        totalTaxPortion: 0
      }
      // if this is created in the paid column then give it a paid date automatically
      // so we know when to archive it
      if (listId === 5) {
        console.log('paid job')
        job.paid_date = new Date().toISOString()
      }
      // set with key
      firebase.database().ref(`lists/${getters.user.group}/list${listId}/${ref}`).set(job)
        .then((data) => {
          commit('setLoading', false)
          commit('setJobDetailModalJob', job)
          commit('toggleShowJobDetailModal')
        })
        .catch((error) => {
          commit('setError', error)
          commit('setLoading', false)
        })
    },
    moveJob ({ commit, getters }, { jobId, oldList, newList }) {
      commit('setLoading', true)
      const job = jobId
      const removeListId = oldList
      const addListId = newList
      const oldRef = firebase.database().ref(`/lists/${getters.user.group}/list${removeListId}/${job}`)
      const newRef = firebase.database().ref(`/lists/${getters.user.group}/list${addListId}/${job}`)
      // call the getter work out new lowest order for this list or returns 0 if empty
      const newListLowestOrder = getters.getListLowestOrder(newList)

      return new Promise((resolve, reject) => {
        oldRef.once('value').then(snap => {
          if (snap.val() !== null) {
            // lets add the payload of the snap to the top of the new list
            const payload = {
              ...snap.val(),
              listId: addListId,
              order: newListLowestOrder
            }
            return newRef.set(payload)
          } else {
            console.log('nothing to move')
            throw new Error('nothing to move')
          }
        }).then(() => {
          return oldRef.set(null)
        }).then(resolve())
          .catch(err => console.log(err))
      })
    },
    updateList ({ commit, getters }, { listId, listData }) {
      const datatosend = listData
      const list = listId
      commit('setLoading', true)

      firebase.database().ref('lists/' + getters.user.group + '/list' + list).set(datatosend)
        .then((data) => {
          commit('setLoading', false)
        })
        .catch((error) => {
          commit('setError', error)
          commit('setLoading', false)
        })
    },
    updateJob ({ commit, getters, dispatch }, { listId, payload }) {
      const group = getters.user.group
      const list = listId
      const job = payload.id
      const data = payload
      firebase.database().ref('lists/' + group + '/list' + list + '/' + job).set(data)
        .then((data) => {
          // if added client name is already in the store.clients array of objects then leave it, if not then add it to FB
          if (getters.clients.filter(function (client) { return client.name === payload.clientName }).length > 0) {
          } else {
            dispatch('createClient', {
              name: payload.clientName
            })
          }
          commit('setLoading', false)
        })
        .catch((error) => {
          commit('setError', error)
          commit('setLoading', false)
        })
    },
    removeJob ({ commit, getters }, { listId, jobId }) {
      const group = getters.user.group
      const list = listId
      firebase.database().ref('lists/' + group + '/list' + list + '/' + jobId).remove()
        .then((data) => {
          commit('setLoading', false)
          this.dispatch('addNotification', { text: 'Job deleted.', type: 'primary' })
        })
        .catch((error) => {
          commit('setError', error)
          commit('setLoading', false)
        })
    },
    async saveQuote ({ commit, getters, dispatch }, payload) {
      const group = getters.user.group
      const quoteId = payload.id
      try {
        return await firebase.database().ref(`quotes/${group}/${quoteId}`).set(payload)
      } catch (error) {
        commit('setError', error)
        Promise.reject(error)
      }
    },
    async saveInvoice ({ commit, getters, dispatch }, payload) {
      const group = getters.user.group
      const invoiceId = payload.id
      try {
        return await firebase.database().ref(`invoices/${group}/${invoiceId}`).set(payload)
      } catch (error) {
        commit('setError', error)
        Promise.reject(error)
      }
    },
    async updateInvoice ({ commit, getters, dispatch }, payload) {
      const group = getters.user.group
      const invoiceId = payload.id
      try {
        return await firebase.database().ref(`invoices/${group}/${invoiceId}`).update(payload)
      } catch (error) {
        commit('setError', error)
        Promise.reject(error)
      }
    },
    createClient ({ commit, getters }, payload) {
      commit('setLoading', true)
      // if there is no client name then dont write it
      // we dont want blank name clients ta
      if (payload.name === '') {
        commit('setLoading', false)
        return
      }
      // all is good we have a name
      // get key
      const ref = firebase.database().ref('clients/' + getters.user.group).push().key
      // build object
      const client = {
        ...payload,
        id: ref,
        creatorId: getters.user.id
      }
      // set with key
      firebase.database().ref('clients/' + getters.user.group + '/' + ref).set(client)
        .then((data) => {
          commit('setLoading', false)
          this.dispatch('addNotification', { text: 'Client created.', type: 'primary' })
        })
        .catch((error) => {
          commit('setError', error)
          commit('setLoading', false)
        })
    },
    onUpdateClient ({ commit, getters }, payload) {
      commit('setLoading', true)
      const group = getters.user.group
      firebase.database().ref('clients/' + group + '/' + payload.id).set(payload)
        .then((data) => {
          commit('setLoading', false)
        })
        .catch((error) => {
          commit('setError', error)
          commit('setLoading', false)
        })
    },
    loadClients ({ commit, getters }) {
      commit('setLoading', true)
      firebase.database().ref('clients/' + getters.user.group).on('value', function (data) {
        const clients = []
        const obj = data.val()
        for (let key in obj) {
          clients.push(obj[key])
        }
        commit('setClients', clients)
        commit('setLoading', false)
      }, error => {
        console.error(error)
        commit('setError', error)
        commit('setLoading', false)
      })
    },
    loadNotifications ({ commit, getters }) {
      commit('setLoading', true)
      firebase.database().ref('notifications/' + getters.user.group).on('value', function (data) {
        const notifications = []
        const obj = data.val()
        for (let key in obj) {
          notifications.push(obj[key])
        }
        commit('setNotifications', notifications)
        commit('setLoading', false)
      }, error => {
        console.error(error)
        commit('setError', error)
        commit('setLoading', false)
      })
    },
    addRepeatJob ({ commit, getters, dispatch }, payload) {
      const group = getters.user.group
      const ref = firebase.database().ref('repeat-jobs/' + getters.user.group).push().key

      const repeatJob = {
        id: ref,
        title: payload.title,
        clientName: payload.clientName,
        amount: payload.amount,
        totalTaxPortion: payload.totalTaxPortion,
        totalNetAmount: payload.totalNetAmount,
        taxIncluded: payload.taxIncluded,
        lineItems: payload.lineItems,
        schedule: {
          frequency: payload.schedule.frequency,
          dayOfMonth: payload.schedule.dayOfMonth,
          monthOfYear: payload.schedule.monthOfYear,
          lastCreated: payload.schedule.lastCreated,
          tz: payload.schedule.tz
        },
        nextRepeats: payload.nextRepeats
      }
      // create entry in the groups repeat jobs node in FB
      firebase.database().ref('repeat-jobs/' + group + '/' + ref).set(repeatJob)
        .then((data) => {
          // if added client name is already in the store.clients array of objects then leave it, if not then add it to FB
          if (getters.clients.filter(function (client) { return client.name === payload.clientName }).length > 0) {
          } else {
            dispatch('createClient', {
              name: payload.clientName
            })
          }
          return data
        })
        .then((data) => {
          // also create a queue item to be picked up by the cron
          const newQueueItem = {
            group: group,
            rJob: ref,
            title: payload.title,
            clientName: payload.clientName,
            amount: payload.amount,
            totalTaxPortion: payload.totalTaxPortion,
            totalNetAmount: payload.totalNetAmount,
            taxIncluded: payload.taxIncluded,
            lineItems: payload.lineItems,
            zuluRepeats: payload.zuluRepeats,
            schedule: payload.schedule,
            paused: false
          }
          return firebase.database().ref('repeat-queue/' + ref).set(newQueueItem)
        })
        .then(() => {
          commit('setLoading', false)
          this.dispatch('addNotification', { text: 'Repeat Job created.', type: 'primary' })
        })
        .catch((error) => {
          commit('setError', error)
          commit('setLoading', false)
        })
      return repeatJob
    },
    onUpdateRepeatJob ({ commit, getters, dispatch }, payload) {
      const group = getters.user.group
      const ref = payload.id

      const repeatJob = {
        id: ref,
        title: payload.title,
        clientName: payload.clientName,
        amount: payload.amount,
        totalTaxPortion: payload.totalTaxPortion,
        totalNetAmount: payload.totalNetAmount,
        taxIncluded: payload.taxIncluded,
        lineItems: payload.lineItems,
        schedule: {
          frequency: payload.schedule.frequency,
          dayOfMonth: payload.schedule.dayOfMonth,
          monthOfYear: payload.schedule.monthOfYear,
          lastCreated: payload.schedule.lastCreated,
          tz: payload.schedule.tz
        },
        nextRepeats: payload.nextRepeats
      }
      // create entry in the groups repeat jobs node in FB
      firebase.database().ref('repeat-jobs/' + group + '/' + ref).set(repeatJob)
        .then((data) => {
          // if added client name is already in the store.clients array of objects then leave it, if not then add it to FB
          if (getters.clients.filter(function (client) { return client.name === payload.clientName }).length > 0) {
          } else {
            dispatch('createClient', {
              name: payload.clientName
            })
          }
          return data
        })
        .then((data) => {
          // also create a queue item to be picked up by the cron
          const newQueueItem = {
            group: group,
            rJob: ref,
            title: payload.title,
            clientName: payload.clientName,
            amount: payload.amount,
            totalTaxPortion: payload.totalTaxPortion,
            totalNetAmount: payload.totalNetAmount,
            taxIncluded: payload.taxIncluded,
            lineItems: payload.lineItems,
            zuluRepeats: payload.zuluRepeats,
            schedule: payload.schedule
          }
          return firebase.database().ref('repeat-queue/' + ref).set(newQueueItem)
        })
        .then(() => {
          commit('setLoading', false)
          this.dispatch('addNotification', { text: 'Repeat Job updated.', type: 'primary' })
        })
        .catch((error) => {
          commit('setError', error)
          commit('setLoading', false)
        })
      return repeatJob
    },
    deleteRepeatJob ({ commit, getters }, repeatJobId) {
      const group = getters.user.group
      const JobId = repeatJobId
      var updates = {}
      updates['repeat-jobs/' + group + '/' + JobId] = null
      updates['repeat-queue/' + JobId] = null
      return new Promise((resolve, reject) => {
        firebase.database().ref().update(updates)
          .then((data) => {
            this.dispatch('addNotification', { text: 'Repeat Job deleted.', type: 'primary' })
            return resolve(true)
          })
          .catch((error) => {
            commit('setError', error)
            return reject(error)
          })
      })
    },
    addExpense ({ commit, getters, dispatch }, payload) {
      const group = getters.user.group
      const ref = firebase.database().ref('expenses/' + getters.user.group).push().key

      const objExpense = {
        id: ref,
        title: payload.title,
        amount: payload.amount
      }

      firebase.database().ref('expenses/' + group + '/' + ref).set(objExpense)
        .then((data) => {
          commit('setLoading', false)
          this.dispatch('addNotification', { text: 'Expense created.', type: 'primary' })
        })
        .catch((error) => {
          commit('setError', error)
          commit('setLoading', false)
        })
      return objExpense
    },
    onUpdateExpense ({ commit, getters }, payload) {
      commit('setLoading', true)
      const group = getters.user.group
      firebase.database().ref('expenses/' + group + '/' + payload.id).set(payload)
        .then((data) => {
          commit('setLoading', false)
          this.dispatch('addNotification', { text: 'Expense updated.', type: 'primary' })
        })
        .catch((error) => {
          commit('setError', error)
          commit('setLoading', false)
        })
    },
    setError ({ commit }, payload) {
      commit('setError', payload)
    },
    clearError ({ commit }) {
      commit('clearError')
    },
    setNotificationState ({ commit }, newState) {
      // simply sets the notification to true or false which controls the display of it
      commit('setNotificationState', newState)
    },
    addNotification ({ commit, getters }, payload) {
      // adds a notification to the queue and will show it if there is no queue
      // get current notification state true false for on or off
      const notification = getters.getNotificationState

      // now add the item to the queue
      commit('addNotificationToQueue', payload)

      // if its not showing then lets get this one and show it
      if (!notification) {
        this.dispatch('shiftNotificationQueue')
        this.dispatch('setNotificationState', true)
      }
    },
    shiftNotificationQueue ({ commit, getters }) {
      // performs array.shift() on the queue
      // get the current queue
      const reducedQueue = getters.getNotificationQueue
      // shift the first item off to use now
      const nextItem = reducedQueue.shift()
      // save the queueitems back to the queue
      commit('setNotificationQueue', reducedQueue)
      // set the current notification to the shifted off item
      commit('setNotificationText', nextItem)
    }
  },
  getters: {
    user (state) {
      return state.user
    },
    loading (state) {
      return state.loading
    },
    error (state) {
      return state.error
    },
    site (state) {
      return state.site
    },
    group (state) {
      return state.group
    },
    clients (state) {
      return state.clients
    },
    list1 (state) {
      return state.list1
    },
    list2 (state) {
      return state.list2
    },
    list3 (state) {
      return state.list3
    },
    list4 (state) {
      return state.list4
    },
    list5 (state) {
      return state.list5
    },
    OrderedJobs1 (state) {
      // uses lodash#sortby for a stable sort and consitency across browsers. chrome was buggy until recently.
      // this method works
      // return state.list2.sort((jobA, jobB) => jobA.order - jobB.order)
      // but we will stick with lodash for a bit
      return sortBy(state.list1, ['order'])
    },
    OrderedJobs2 (state) {
      // uses lodash sortby for a stable sort chrome was buggy using the A > B method.
      return sortBy(state.list2, ['order'])
    },
    OrderedJobs3 (state) {
      // uses lodash sortby for a stable sort chrome was buggy using the A > B method.
      return sortBy(state.list3, ['order'])
    },
    OrderedJobs4 (state) {
      // uses lodash sortby for a stable sort chrome was buggy using the A > B method.
      return sortBy(state.list4, ['order'])
    },
    OrderedJobs5 (state) {
      // uses lodash sortby for a stable sort chrome was buggy using the A > B method.
      return sortBy(state.list5, ['order'])
    },
    getListLowestOrder: (state, getters) => (listId) => {
      // get the lowest order for this listId or return 0 if empty
      if (state['list' + listId].length) {
        return getters['OrderedJobs' + listId][0].order - 1
      } else {
        return 0
      }
    },
    latestJobKey (state) {
      return state.latestJobKey
    },
    notifications (state) {
      return state.notifications
    },
    notificationUnreadTotal (state, getters) {
      return getters.notifications.filter(notification => notification.status === 'unread').length
    },
    getNotificationState (state) {
      return state.notification
    },
    getNotificationText (state) {
      return state.notificationText
    },
    getNotificationQueue (state) {
      return state.notificationQueue
    },
    showSplitInvoiceModal (state) {
      return state.showSplitInvoiceModal
    },
    getSplitInvoiceModalJob (state) {
      return state.splitInvoiceModalJob
    },
    showMultiMergeModal (state) {
      return state.showMultiMergeModal
    },
    getMultiMergeModalJob (state) {
      return state.multiMergeModalJob
    },
    showJobDetailModal (state) {
      return state.showJobDetailModal
    },
    getJobDetailModalJob (state) {
      return state.jobDetailModalJob
    },
    getListIdFromJobId: (state) => (jobId) => {
      const allLists = state.list1.concat(state.list2, state.list3, state.list4, state.list5)
      if (allLists.length > 0) {
        const job = allLists.find(x => x.id === jobId)
        if (job) {
          return job.listId
        }
      }
      return null
    },
    getHumanError (state) {
      // use for any technical looking error codes
      switch (state.error.code) {
        case 'auth/user-not-found':
          return 'Sorry, no account found. Did you mean to create an account?'
        default:
          return state.error.message
      }
    },
    hasPremiumAccess (state) {
      const accessLevel = ((state.group.subscription || {}).access || {}).level || 'starter'
      // optional?.chaining? should be used here when available eg
      // const accessLevel = this.group?.subscription?.access?.level || 'starter'
      if (accessLevel === 'premium') {
        return true
      } else {
        return false
      }
    },
    hasProAccess (state) {
      const accessLevel = ((state.group.subscription || {}).access || {}).level || 'starter'
      // optional?.chaining? should be used here when available eg
      // const accessLevel = this.group?.subscription?.access?.level || 'starter'
      if (accessLevel === 'pro') {
        return true
      } else {
        return false
      }
    },
    hasStarterAccess (state) {
      const accessLevel = ((state.group.subscription || {}).access || {}).level || 'starter'
      // optional?.chaining? should be used here when available eg
      // const accessLevel = this.group?.subscription?.access?.level || 'starter'
      if (accessLevel === 'starter') {
        return true
      } else {
        return false
      }
    },
    jobFilter (state) {
      return state.jobFilter
    }
  }
})
